import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, X, Search, Plus } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { ROUTE } from '@/config';
import { useFetchWebhooksEvents, useCreateWebhooks } from '@/hooksApi';
import type { WebHooksProperty } from '@/types';

interface Header {
  id: string;
  key: string;
  value: string;
}

const SettingsWebhooksCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: eventsData } = useFetchWebhooksEvents();
  const { mutate: createWebhook, isLoading: isCreating } = useCreateWebhooks();

  const triggerOptions = eventsData?.object?.items?.map((event) => event.title) || [];

  const [url, setUrl] = useState('');
  const [selectedTriggers, setSelectedTriggers] = useState<string[]>([]);
  const [headers, setHeaders] = useState<Header[]>([]);
  const [triggerSearch, setTriggerSearch] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  const filteredTriggers = triggerOptions.filter((trigger) =>
    trigger.toLowerCase().includes(triggerSearch.toLowerCase()),
  );

  const toggleTrigger = (trigger: string) => {
    setSelectedTriggers((prev) =>
      prev.includes(trigger) ? prev.filter((t) => t !== trigger) : [...prev, trigger],
    );
    setFieldErrors(prev => ({ ...prev, triggers: false }));
  };

  const addHeader = () => {
    setHeaders((prev) => [...prev, { id: Date.now().toString(), key: '', value: '' }]);
  };

  const removeHeader = (id: string) => {
    setHeaders((prev) => prev.filter((h) => h.id !== id));
  };

  const updateHeader = (id: string, field: 'key' | 'value', value: string) => {
    setHeaders((prev) => prev.map((h) => (h.id === id ? { ...h, [field]: value } : h)));
  };

  const handleCancel = () => {
    navigate(ROUTE.SETTINGS_WEBHOOKS);
  };

  const handleCreate = () => {
    const errors: Record<string, boolean> = {};
    if (!url.trim()) errors.url = true;
    if (selectedTriggers.length === 0) errors.triggers = true;
    const validHeaders = headers.filter((h) => h.key.trim() && h.value.trim());
    // Headers are optional — no validation error if none provided

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const missing = Object.keys(errors).map(k => String(t(k))).join(', ');
      toast({ title: t('required_fields_missing'), description: missing, variant: 'destructive' });
      return;
    }

    const eventUids =
      eventsData?.object?.items
        ?.filter((event) => selectedTriggers.includes(event.title))
        .map((event) => event.uid) || [];

    createWebhook(
      {
        url: url.trim(),
        event_uids: eventUids,
        properties: validHeaders.map((h) => ({
          type: 'header',
          key: h.key,
          value: h.value,
        })) as WebHooksProperty[],
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['webhooks']);
          toast({ title: t('webhook_created') });
          navigate(ROUTE.SETTINGS_WEBHOOKS);
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.message || error?.message || t('operation_failed');
          toast({ title: t('error_title'), description: msg, variant: 'destructive' });
        },
      },
    );
  };

  const inputClass = (field?: string) =>
    `w-full px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-sd-ring focus:border-transparent placeholder:text-sd-muted-foreground ${
      field && fieldErrors[field] ? 'border-red-500 bg-red-50' : 'border-sd-input bg-sd-input-bg'
    }`;

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb
          moduleName='web_hooks'
          moduleRoute={ROUTE.SETTINGS_WEBHOOKS}
          action='create_webhook'
        />

        <h1 className='text-xl font-semibold text-sd-foreground'>{t('create_webhook')}</h1>

        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          <div className='px-6 py-4 border-b border-sd-border flex items-center gap-3'>
            <button
              onClick={handleCancel}
              className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <ArrowLeft className='h-4 w-4' />
            </button>
            <h2 className='text-sm font-semibold text-sd-foreground'>{t('webhook_details')}</h2>
          </div>
          <div className='p-6 space-y-5'>
            {/* URL */}
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>{t('url')} *</label>
              <input
                type='url'
                value={url}
                onChange={(e) => { setUrl(e.target.value); setFieldErrors(prev => ({ ...prev, url: false })); }}
                placeholder='https://example.com/webhook'
                className={inputClass('url')}
              />
              {fieldErrors.url && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
            </div>

            {/* Triggers */}
            <div className='space-y-1.5'>
              <label className='text-xs font-medium text-sd-muted-foreground'>
                {t('triggers')} *
              </label>
              <div className={`rounded-lg border bg-sd-input-bg ${fieldErrors.triggers ? 'border-red-500' : 'border-sd-input'}`}>
                <div className='relative px-3 py-2 border-b border-sd-input'>
                  <Search className='absolute left-5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-sd-muted-foreground' />
                  <input
                    type='text'
                    value={triggerSearch}
                    onChange={(e) => setTriggerSearch(e.target.value)}
                    placeholder={t('search_triggers')}
                    className='w-full pl-6 text-sm bg-transparent focus:outline-none placeholder:text-sd-muted-foreground'
                  />
                </div>
                <div className='flex flex-wrap gap-2 max-h-[200px] overflow-y-auto p-3'>
                  {filteredTriggers.length === 0 && (
                    <p className='text-xs text-sd-muted-foreground'>{t('no_triggers_match')}</p>
                  )}
                  {filteredTriggers.map((trigger) => {
                    const selected = selectedTriggers.includes(trigger);
                    return (
                      <button
                        key={trigger}
                        type='button'
                        onClick={() => toggleTrigger(trigger)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
                          selected
                            ? 'bg-sd-primary text-sd-primary-foreground border-sd-primary'
                            : 'bg-sd-muted text-sd-muted-foreground border-transparent hover:bg-sd-accent hover:text-sd-accent-foreground'
                        }`}
                      >
                        {trigger}
                      </button>
                    );
                  })}
                </div>
              </div>
              {fieldErrors.triggers && <p className='text-xs text-red-500 mt-1'>{t('this_field_is_required')}</p>}
            </div>

            {/* Headers */}
            <div className='space-y-2'>
              <label className='text-xs font-medium text-sd-muted-foreground'>
                {t('headers')}
              </label>
              <div className='space-y-2'>
                {headers.map((header) => (
                  <div key={header.id} className='flex items-center gap-2'>
                    <input
                      type='text'
                      value={header.key}
                      onChange={(e) => updateHeader(header.id, 'key', e.target.value)}
                      placeholder={t('key')}
                      className={`${inputClass()} flex-1`}
                    />
                    <input
                      type='text'
                      value={header.value}
                      onChange={(e) => updateHeader(header.id, 'value', e.target.value)}
                      placeholder={t('value')}
                      className={`${inputClass()} flex-1`}
                    />
                    <button
                      type='button'
                      onClick={() => removeHeader(header.id)}
                      className='p-2 rounded-lg text-sd-muted-foreground hover:text-sd-destructive hover:bg-sd-destructive/10 transition-colors shrink-0'
                    >
                      <X className='h-4 w-4' />
                    </button>
                  </div>
                ))}
                <div onClick={addHeader} className='flex items-center gap-2 cursor-pointer group'>
                  <div className='flex-1 h-10 px-3 rounded-lg border border-dashed border-sd-input bg-sd-input-bg flex items-center text-sm text-sd-muted-foreground/50 group-hover:border-sd-foreground/20 group-hover:text-sd-muted-foreground transition-colors'>
                    {t('key')}
                  </div>
                  <div className='flex-1 h-10 px-3 rounded-lg border border-dashed border-sd-input bg-sd-input-bg flex items-center text-sm text-sd-muted-foreground/50 group-hover:border-sd-foreground/20 group-hover:text-sd-muted-foreground transition-colors'>
                    {t('value')}
                  </div>
                  <div className='p-2 shrink-0 rounded-lg text-sd-muted-foreground/50 group-hover:text-sd-primary-foreground group-hover:bg-sd-primary transition-colors'>
                    <Plus className='h-4 w-4' />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='px-6 py-4 border-t border-sd-border flex justify-end gap-3'>
            <button
              onClick={handleCancel}
              className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className='px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed'
            >
              {isCreating ? t('loading') : t('create_webhook')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsWebhooksCreate;
